/**
 * Task #3844 — Pannello "Delega consapevole" nella config del Personal Assistant.
 *
 * Mostra il diff concreto tra i poteri (tool MCP) dell'agente e i permessi
 * RBAC dell'utente assegnato, e registra la scelta dell'admin:
 *  - "Consenti delega": il PA agisce anche oltre i permessi dell'utente
 *    (azioni oltre-permessi tracciate in audit);
 *  - "Limita ai permessi dell'utente": intersezione — i tool fuori perimetro
 *    spariscono dalla chat e sono rifiutati server-side.
 *
 * DTO condivisi da @easyflow/shared (paDelegationStatusSchema & co.).
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Loader2,
  Lock,
  ShieldCheck,
  Unlock,
} from 'lucide-react';
import { paDelegationStatusSchema, paDelegationAuditListSchema, paDelegationAckBodySchema } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { emitPersonalAssistantUpdated } from '@/lib/personal-assistant-events';
import { getApiErrorMessage } from '../../lib/utils';
import { PaConfigInfo } from './PaConfigInfo';
import type {
  PaDelegationStatusDto,
  PaDelegationAuditListDto,
  PaDelegationMode,
  PaDelegationAuditEntry,
} from '@easyflow/shared';

interface PaDelegationPanelProps {
  assistantId: string;
  configurationDirty?: boolean;
  disabled?: boolean;
}

const EVENT_LABELS: Record<PaDelegationAuditEntry['event_type'], string> = {
  mode_ack: 'Scelta confermata',
  delegated_action: 'Azione delegata',
  drift_detected: 'Cambio permessi rilevato',
};

function formatTs(ts: string | Date | null): string {
  if (!ts) return '—';
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString('it-IT');
}

export function PaDelegationPanel({ assistantId, configurationDirty = false, disabled = false }: PaDelegationPanelProps) {
  const { toast } = useToast();
  const [status, setStatus] = useState<PaDelegationStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<PaDelegationMode>('delegated');
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState<PaDelegationAuditEntry[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get<PaDelegationStatusDto>(`/personal-assistant/agents/${assistantId}/delegation`);
      const data = paDelegationStatusSchema.parse(res.data);
      setStatus(data);
      setSelectedMode(data.mode);
    } catch (error) {
      setLoadError(getApiErrorMessage(error) || 'Impossibile caricare lo stato della delega');
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await apiClient.get<PaDelegationAuditListDto>(
        `/personal-assistant/agents/${assistantId}/delegation/audit?limit=50`,
      );
      const data = paDelegationAuditListSchema.parse(res.data);
      setAuditItems(data.items);
    } catch (error) {
      toast({
        title: 'Errore',
        description: getApiErrorMessage(error) || 'Impossibile caricare lo storico',
        variant: 'destructive',
      });
    } finally {
      setAuditLoading(false);
    }
  }, [assistantId, toast]);

  const handleConfirm = async () => {
    if (configurationDirty || disabled || !status || saving) return;
    setSaving(true);
    try {
      const res = await apiClient.post<PaDelegationStatusDto>(
        `/personal-assistant/agents/${assistantId}/delegation`,
        paDelegationAckBodySchema.parse({ mode: selectedMode, expected_hash: status.current_hash }),
      );
      const data = paDelegationStatusSchema.parse(res.data);
      setStatus(data);
      setSelectedMode(data.mode);
      emitPersonalAssistantUpdated({ assistantId });
      toast({
        title: 'Scelta registrata',
        description:
          selectedMode === 'delegated'
            ? 'Il PA agirà con i poteri dell\'agente; le azioni oltre i permessi dell\'utente saranno tracciate in audit.'
            : 'Il PA sarà limitato ai permessi dell\'utente assegnato: i tool fuori perimetro non saranno né visibili né eseguibili.',
      });
      if (auditOpen) void loadAudit();
    } catch (error) {
      toast({
        title: 'Errore',
        description: getApiErrorMessage(error) || 'Impossibile registrare la scelta',
        variant: 'destructive',
      });
      void loadStatus();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento delega…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3 py-6 text-center">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button variant="outline" size="sm" onClick={() => void loadStatus()}>Riprova</Button>
      </div>
    );
  }

  if (!status) return null;

  if (!status.assigned_user_id) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Nessun utente assegnato: la delega si configura dopo aver assegnato il PA a un utente
          (scheda "Prompt &amp; Utente").
        </CardContent>
      </Card>
    );
  }

  const userLabel = status.assigned_user_name || 'l\'utente assegnato';
  const beyondCount = status.beyond_tools.length;
  const unverifiable = status.tools?.filter(tool => tool.availability === 'unverifiable') ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1">
        <h3 className="text-sm font-medium">Deve rispettare i permessi dell’utente?</h3>
        <PaConfigInfo label="Informazioni sulla modalità di autorizzazione">Con la delega il PA può usare strumenti selezionati anche oltre i permessi del ruolo. Non cambia il ruolo dell’utente né abilita strumenti esclusi.</PaConfigInfo>
      </div>
      {configurationDirty && <p role="alert" className="text-sm text-warning">Salva la configurazione prima di confermare la modalità. Il riepilogo seguente riguarda solo la configurazione salvata, non la bozza.</p>}
      {(status.diagnostic || unverifiable.length > 0) && <div role="alert" className="rounded-md border border-warning/40 p-3 text-sm">
        Confronto non verificabile{unverifiable.length > 0 ? ` per ${unverifiable.length} strumenti` : ''}. {status.diagnostic}
      </div>}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={status.mode === 'delegated' ? 'default' : 'secondary'}>
          {status.mode === 'delegated' ? (
            <span className="inline-flex items-center gap-1"><Unlock className="h-3 w-3" /> Modalità attiva: Consenti delega</span>
          ) : (
            <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Modalità attiva: Rispetta i permessi dell’utente</span>
          )}
        </Badge>
        {status.drift ? (
          <Badge variant="destructive" className="inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Permessi cambiati: riconferma richiesta
          </Badge>
        ) : status.ack_at ? (
          <Badge variant="outline" className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Confermata il {formatTs(status.ack_at)}
            {status.ack_by_name ? ` da ${status.ack_by_name}` : ''}
          </Badge>
        ) : (
          <Badge variant="outline" className="inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Mai confermata (default: delega)
          </Badge>
        )}
      </div>

      <details className="rounded-lg border p-3">
        <summary className="cursor-pointer text-sm font-medium">Dettaglio poteri del salvato ({beyondCount} oltre il ruolo)</summary>
        <Card className="mt-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Poteri oltre i permessi di {userLabel}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {status.diagnostic || unverifiable.length > 0 ? (
            <p className="text-sm text-muted-foreground">Il confronto non è completo: non è possibile dichiarare che tutti gli strumenti rientrino nei permessi dell’utente.</p>
          ) : status.user_is_privileged ? (
            <p className="text-sm text-muted-foreground">
              {userLabel} è amministratore: nessun potere del PA eccede i suoi permessi.
            </p>
          ) : beyondCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              Tutti i {status.total_tools} tool di questo PA rientrano nei permessi di {userLabel}.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Questo PA può usare <strong>{beyondCount}</strong> tool su {status.total_tools} che{' '}
                {userLabel} non può usare nell'app:
              </p>
              <ul className="max-h-56 overflow-y-auto divide-y rounded-md border text-sm">
                {status.beyond_tools.map((entry) => (
                  <li key={JSON.stringify([entry.server_id, entry.tool_name])} className="flex items-center justify-between gap-2 px-3 py-1.5">
                    <span className="font-mono text-xs">{entry.tool_name}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {entry.required_permission}
                    </Badge>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
        </Card>
      </details>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          aria-pressed={selectedMode === 'delegated'}
          disabled={saving || disabled}
          onClick={() => setSelectedMode('delegated')}
          className={`rounded-lg border p-3 text-left transition-colors ${
            selectedMode === 'delegated' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
          }`}
        >
          <div className="flex items-center gap-2 font-medium text-sm">
            <Unlock className="h-4 w-4" /> Consenti delega
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Può usare strumenti selezionati anche oltre il suo ruolo
          </p>
        </button>
        <button
          type="button"
          aria-pressed={selectedMode === 'intersection'}
          disabled={saving || disabled}
          onClick={() => setSelectedMode('intersection')}
          className={`rounded-lg border p-3 text-left transition-colors ${
            selectedMode === 'intersection' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
          }`}
        >
          <div className="flex items-center gap-2 font-medium text-sm">
            <Lock className="h-4 w-4" /> Rispetta i permessi dell’utente
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Gli strumenti selezionati restano limitati dal suo ruolo
          </p>
        </button>
      </div>
      {selectedMode !== status.mode && <p className="text-sm text-muted-foreground">Scelta non ancora applicata. La modalità attiva resta invariata fino alla conferma.</p>}
      <p className="text-sm font-medium">Effetto immediato: Annulla non revoca una conferma già effettuata</p>

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const next = !auditOpen;
            setAuditOpen(next);
            if (next && auditItems === null) void loadAudit();
          }}
        >
          <History className="h-4 w-4 mr-1" /> Storico delega
        </Button>
        <div className="flex items-center gap-1">
          <Button size="sm" onClick={() => void handleConfirm()} disabled={saving || configurationDirty || disabled}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Conferma modalità ora
          </Button>
          <PaConfigInfo label="Informazioni sulla conferma immediata">Applica subito la modalità alla configurazione salvata. Salva prima eventuali modifiche a utente o strumenti.</PaConfigInfo>
        </div>
      </div>

      {auditOpen && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Storico riconferme e azioni delegate</CardTitle>
          </CardHeader>
          <CardContent>
            {auditLoading ? (
              <div className="flex items-center text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Caricamento…
              </div>
            ) : !auditItems || auditItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>
            ) : (
              <ul className="divide-y text-sm max-h-64 overflow-y-auto">
                {auditItems.map((item) => (
                  <li key={item.id} className="py-2 space-y-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{EVENT_LABELS[item.event_type]}</span>
                      <span className="text-xs text-muted-foreground">{formatTs(item.created_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {item.event_type === 'delegated_action' && item.tool_name
                        ? `Tool ${item.tool_name} eseguito per conto di ${item.assigned_user_name ?? 'utente'} con poteri delegati (permesso mancante: ${item.required_permission ?? '—'})`
                        : item.event_type === 'mode_ack'
                          ? `Modalità "${typeof item.detail?.mode === 'string' ? item.detail.mode : '—'}" confermata${item.actor_user_name ? ` da ${item.actor_user_name}` : ''}`
                          : 'Il diff dei poteri è cambiato: richiesta riconferma'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PaDelegationPanel;
